import { Refine } from '@refinedev/core';
import { RefineKbar, RefineKbarProvider } from '@refinedev/kbar';
import {
  useNotificationProvider,
  RefineSnackbarProvider,
} from '@refinedev/mui';
import CssBaseline from '@mui/material/CssBaseline';
import GlobalStyles from '@mui/material/GlobalStyles';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import routerBindings, {
  UnsavedChangesNotifier,
  DocumentTitleHandler,
} from '@refinedev/react-router';
import { axiosInstance } from '@/core/services';
import { ColorModeContextProvider } from '@/core/theme';
import { AuthProvider } from '@refinedev/core';
import { dataProvider, useResources } from '../data';
import { useI18nProvider } from '../i18n';
import { useAccessControlProvider } from '../auth/useAccessControlProvider';

type RefineProviderProps = {
  authProvider: AuthProvider;
  children: React.ReactNode;
};

export const RefineProvider = ({
  authProvider,
  children,
}: RefineProviderProps) => {
  const resources = useResources();
  const i18nProvider = useI18nProvider();

  const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';
  const provider = dataProvider({ baseUrl, axiosInstance });

  const accessControlProvider = useAccessControlProvider();

  return (
    <RefineKbarProvider>
      <ColorModeContextProvider>
        <CssBaseline />
        <GlobalStyles styles={{ html: { WebkitFontSmoothing: 'auto' } }} />
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <RefineSnackbarProvider>
            <Refine
              dataProvider={provider}
              notificationProvider={useNotificationProvider}
              routerProvider={routerBindings}
              authProvider={authProvider}
              accessControlProvider={accessControlProvider}
              i18nProvider={i18nProvider}
              resources={resources}
              options={{
                syncWithLocation: true,
                warnWhenUnsavedChanges: true,
                useNewQueryKeys: true,
                projectId: 'F4prX7-PVEudS-qeJqDe',
              }}
            >
              {children}
              <RefineKbar />
              <UnsavedChangesNotifier />
              <DocumentTitleHandler />
            </Refine>
          </RefineSnackbarProvider>
        </LocalizationProvider>
      </ColorModeContextProvider>
    </RefineKbarProvider>
  );
};
